import { CONFIG } from "../../../config";
import axios from "axios";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

// Dev logging (mirrors pattern from connected status hook)
const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => { if (isDevelopment) console.log(message, data); };

/**
 * Custom hook for fetching agent analytics data (refactored)
 * @param {Object} queries - Query parameters (year, month, allocatedToUser?, etc.)
 * @param {Object} options - React Query options overrides
 * @returns {Object} React Query result object
 */
export const useGetAgentAnalytics = (queries = {}, options = {}) => {
  // Sanitize queries
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== 'object') {
      devLog('[useGetAgentAnalytics] Invalid queries parameter, using empty object');
      return {};
    }
    // Normalize alternate range keys: support fromDate/toDate -> from/to
    const normalized = { ...queries };
    if (queries.fromDate && !queries.from) normalized.from = queries.fromDate;
    if (queries.toDate && !queries.to) normalized.to = queries.toDate;
    return normalized;
  }, [queries]);

  // Merge default query options
  const queryOptions = useMemo(() => ({
    enabled: true,
    refetchOnWindowFocus: false,
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    ...options,
  }), [options]);

  // Build stable query key (sorted entries, ignoring undefined/null/empty)
  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b));
    return ["agentAnalytics", ...sortedEntries.flat()];
  }, [validatedQueries]);

  // Fetcher
  const fetchAgentAnalytics = useCallback(async () => {
    try {
      // Remove empty values; ensure arrays preserved
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(([, v]) => (
          v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0) && v !== ''
        ))
      );

      // Enforce required params: either year+month OR from/to (or fromDate/toDate)
      const hasYearMonth = !!cleanParams.year && !!cleanParams.month;
      const hasRange = (!!cleanParams.from && !!cleanParams.to) || (!!cleanParams.fromDate && !!cleanParams.toDate);
      if (!hasYearMonth && !hasRange) {
        throw new Error('Either year+month or from+to (or fromDate+toDate) are required');
      }

      devLog('[useGetAgentAnalytics] Fetching with params:', cleanParams);

      const response = await axios.get(`${API_HOST_CRM}/analytics/agent-analytics`, {
        params: cleanParams,
        timeout: 30000, // 30s timeout
      });

      devLog('[useGetAgentAnalytics] Response:', response.data);

      if (!response.data) throw new Error('No data received from server');
      return response.data;
    } catch (error) {
      const isTimeoutError = error.code === 'ECONNABORTED';
      let message = 'Error fetching agent analytics';
      if (isTimeoutError) message += ': Request timeout';
      else if (error.response) message += `: Server responded with ${error.response.status}`;
      console.error(message, {
        originalError: error,
        params: validatedQueries,
        timestamp: new Date().toISOString()
      });
      throw new Error(message);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchAgentAnalytics,
    enabled: queryOptions.enabled && ((!!validatedQueries.year && !!validatedQueries.month) || ((!!validatedQueries.from && !!validatedQueries.to) || (!!validatedQueries.fromDate && !!validatedQueries.toDate))),
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    select: (data) => data || null,
    onError: (error) => {
      console.error('Error in useGetAgentAnalytics:', {
        error: error.message,
        queries: validatedQueries,
        timestamp: new Date().toISOString()
      });
    }
  });
};
